import React, { useMemo } from 'react';
import {
  set,
  get,
  flow,
  filter,
  getOr,
  sumBy,
  reject,
  findIndex,
  isNull,
  keyBy
} from 'lodash/fp';
import { List } from 'rsuite';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Item from '../PicklistItem';
import guestQuantityLimit from '../../../helpers/guestQuantityLimit';
import { parseInt } from '../../../helpers/utils';

const ItemsList = styled(List)`
  padding: 10px;
  text-align: center;
`;

const CategoryItem = styled(List.Item)`
  padding: 0;
  box-shadow: none;
  background-color: #efefef;
  > .rs-list-item-content {
    > div:first-child {
      padding: 10px;
      text-align: left;
      font-weight: 500;
      > h6 {
        font-size: 18px;
        text-transform: uppercase;
        margin-bottom: 5px;
      }
    }
  }
`;

const toOrderItemUniqKey = (picklistId, picklistCategoryId, itemId) =>
  `${picklistId}_${picklistCategoryId}_${itemId}`;

const toOrderItemsByUniqKey = (picklistId, orderItems) =>
  keyBy(
    ({ itemId, picklistCategoryId }) =>
      toOrderItemUniqKey(picklistId, picklistCategoryId, itemId),
    orderItems
  );

const getCategoryLimit = (guestInfo, limitRules, picklistLimit) => {
  const hasLimit = !isNull(limitRules);
  const categoryLimit = guestQuantityLimit(guestInfo, limitRules);

  if (hasLimit && picklistLimit > 0 && categoryLimit > picklistLimit)
    return picklistLimit;

  return categoryLimit;
};

const PicklistCategory = ({
  data,
  guestInfo,
  picklistId,
  items,
  orderItems,
  picklistLimit,
  picklistLimitReached,
  onChangeOrderItems
}) => {
  const { id, name, limitRules } = data;
  const orderItemsByKey = toOrderItemsByUniqKey(picklistId, orderItems);
  const quantityLimit = getCategoryLimit(guestInfo, limitRules, picklistLimit);

  const addedQuantity = useMemo(
    () =>
      flow(
        filter({ picklistCategoryId: id }),
        sumBy('quantity')
      )(orderItems),
    [orderItems]
  );

  const limitReached = quantityLimit > 0 && addedQuantity >= quantityLimit;

  const handleOrderItemChange = orderItem => {
    const { itemId, picklistCategoryId, quantity } = orderItem;
    const itemIndex = findIndex({ itemId, picklistCategoryId }, orderItems);
    const itemExists = itemIndex >= 0;
    if (itemExists) {
      if (!get('id', orderItem) && quantity < 1) {
        onChangeOrderItems(reject({ itemId, picklistCategoryId }, orderItems));
      } else onChangeOrderItems(set(itemIndex, orderItem, orderItems));
    } else if (quantity > 0) onChangeOrderItems([...orderItems, orderItem]);
  };

  return (
    <CategoryItem key={`category-${id}`}>
      <h6 style={{ padding: 10, textAlign: 'center', background: '#ddd' }}>
        {name} {quantityLimit > 0 && ` (${quantityLimit - addedQuantity})`}
      </h6>
      <ItemsList>
        {items.map(each => {
          const { inventorySnapshot = null } = each;
          const itemId = parseInt(each.itemId);
          const uniqKey = toOrderItemUniqKey(picklistId, id, itemId);
          const defaultItem = {
            itemId,
            picklistCategoryId: id,
            inventorySnapshot,
            quantity: 0
          };
          const orderItem = getOr(defaultItem, uniqKey, orderItemsByKey);
          return (
            <Item
              key={`item-${each.itemId}-${each.position}`}
              data={each}
              orderItem={orderItem}
              limitReached={picklistLimitReached || limitReached}
              categoryLimit={quantityLimit}
              onChangeOrderItem={handleOrderItemChange}
            />
          );
        })}
      </ItemsList>
    </CategoryItem>
  );
};

const IntOrString = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

PicklistCategory.propTypes = {
  data: PropTypes.shape({
    id: IntOrString.isRequired,
    name: PropTypes.string.isRequired,
    limitRules: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  guestInfo: PropTypes.shape({}).isRequired,
  picklistId: PropTypes.number.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  orderItems: PropTypes.arrayOf(PropTypes.shape({})),
  picklistLimit: PropTypes.number,
  picklistLimitReached: PropTypes.bool.isRequired,
  onChangeOrderItems: PropTypes.func.isRequired
};

PicklistCategory.defaultProps = {
  orderItems: [],
  picklistLimit: 0
};

export default PicklistCategory;
